import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  CancelWorkspaceDeletionDocument,
  ScheduleWorkspaceDeletionDocument,
  useAppData,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, WorkspaceEvent, trackEvent } from "#app/lib/analytics";

/** Days between scheduling and the purge — mirrors core's grace period. */
export const WORKSPACE_DELETION_GRACE_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days until `iso`, never below zero. */
function daysUntil(iso: string, now = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / DAY_MS));
}

/** "30 days" / "1 day" / "today". */
export function daysLeftLabel(iso: string): string {
  const days = daysUntil(iso);
  if (days === 0) return "today";
  return days === 1 ? "1 day" : `${days} days`;
}

/**
 * The two Owner actions on a workspace's deletion — schedule and restore —
 * each followed by the account-level refetch that flips the shell to (or
 * from) the pending screen: `deletion` rides the `workspaces` list the
 * provider holds as state, so a successful mutation alone changes nothing on
 * screen. There is no "delete now": the purge belongs to the daily sweep.
 */
export function useWorkspaceDeletion(workspaceId: string) {
  const { refetch } = useAppData();
  const [scheduleMutation, { loading: scheduling }] = useMutation(
    ScheduleWorkspaceDeletionDocument,
  );
  const [cancelMutation, { loading: cancelling }] = useMutation(
    CancelWorkspaceDeletionDocument,
  );

  const scheduleDeletion = useCallback(
    async (confirmName: string): Promise<boolean> => {
      try {
        await scheduleMutation({ variables: { id: workspaceId, confirmName } });
        trackEvent({
          context: AnalyticsContext.Workspace,
          event: WorkspaceEvent.ScheduleDeletion,
        });
        await refetch();
        toastManager.add({
          title: "Deletion scheduled",
          description: `This workspace deletes in ${WORKSPACE_DELETION_GRACE_DAYS} days.`,
          type: "success",
        });
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to schedule workspace deletion");
        return false;
      }
    },
    [scheduleMutation, workspaceId, refetch],
  );

  const cancelDeletion = useCallback(async (): Promise<boolean> => {
    try {
      await cancelMutation({ variables: { id: workspaceId } });
      trackEvent({
        context: AnalyticsContext.Workspace,
        event: WorkspaceEvent.CancelDeletion,
      });
      await refetch();
      return true;
    } catch (err) {
      toastMutationError(err, "Failed to restore workspace");
      return false;
    }
  }, [cancelMutation, workspaceId, refetch]);

  return {
    scheduleDeletion,
    cancelDeletion,
    busy: scheduling || cancelling,
  };
}
